import React, { useState, useEffect, useRef } from 'react';

const API_BASE = process.env.REACT_APP_API_BASE;
const API_KEY = process.env.REACT_APP_API_KEY;

export default function ReactionTooltip({ reviewId, emoji, user, children }) {
  const [tooltipData, setTooltipData] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const timeoutRef = useRef(null);
  const tooltipRef = useRef(null);

  const fetchReactionDetails = async () => {
    if (loading || tooltipData) return; // Don't fetch if already loading or cached

    setLoading(true);
    try {
      const headers = {
        'X-API-Key': API_KEY,
        'Content-Type': 'application/json',
      };

      if (user) {
        headers['X-User-ID'] = user.id;
      }

      const response = await fetch(`${API_BASE}/v1/reviews/${reviewId}/reactions/details`, {
        headers,
      });

      if (response.ok) {
        const data = await response.json();
        setTooltipData(data);
      }
    } catch (error) {
      console.error('Failed to fetch reaction details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMouseEnter = () => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Show tooltip after slight delay (Discord-style)
    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
      fetchReactionDetails();
    }, 300);
  };

  const handleMouseLeave = () => {
    // Clear the show timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Hide tooltip after slight delay to allow moving to tooltip
    timeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, 100);
  };

  const handleTooltipMouseEnter = () => {
    // Keep tooltip visible when hovering over it
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const handleTooltipMouseLeave = () => {
    // Hide tooltip when leaving the tooltip itself
    setIsVisible(false);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const renderTooltipContent = () => {
    if (loading) {
      return (
        <div className="text-xs text-gray-500">
          Loading...
        </div>
      );
    }

    if (!tooltipData || !tooltipData.reaction_details[emoji]) {
      return null;
    }

    const emojiData = tooltipData.reaction_details[emoji];
    const users = emojiData.users || [];

    return (
      <div className="text-xs">
        <div className="flex items-center gap-1 mb-1">
          <span className="text-sm">{emoji}</span>
          <span className="font-medium">{emojiData.count}</span>
        </div>
        <div className="text-gray-700">
          {users.length > 0 ? users.join(', ') : 'No users'}
        </div>
      </div>
    );
  };

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>

      {isVisible && (
        <div
          ref={tooltipRef}
          onMouseEnter={handleTooltipMouseEnter}
          onMouseLeave={handleTooltipMouseLeave}
          className="
            absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2
            bg-gray-900 text-white text-xs rounded-lg px-3 py-2
            shadow-lg z-30 whitespace-nowrap max-w-xs
            border border-gray-700
          "
          style={{ minWidth: '120px' }}
        >
          {renderTooltipContent()}

          {/* Tooltip arrow */}
          <div className="
            absolute top-full left-1/2 transform -translate-x-1/2
            border-4 border-transparent border-t-gray-900
          "></div>
        </div>
      )}
    </div>
  );
}